import { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import {
  School, Users, UserPlus, GraduationCap, Loader2, Mail, LogOut,
  MessageCircle, PauseCircle, Copy, Download, Sparkles,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function fmtDate(iso) {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString(); } catch { return iso; }
}
function fmtCurrency(cents, currency = 'ZAR') {
  if (cents === null || cents === undefined) return 'Not set';
  return `${currency === 'ZAR' ? 'R' : currency + ' '}${(cents / 100).toFixed(2)}`;
}

/**
 * School admin dashboard (PFP-only schools).
 * READ-ONLY parent stats + invite parent/therapist actions + seats overview.
 */
export default function SchoolAdminHome() {
  const navigate = useNavigate();
  const [overview, setOverview] = useState(null);
  const [parents, setParents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dlg, setDlg] = useState(null); // 'invite-parent' | 'invite-therapist'
  const [pEmail, setPEmail] = useState('');
  const [pName, setPName] = useState('');
  const [tEmail, setTEmail] = useState('');
  const [tName, setTName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [buySeatsCount, setBuySeatsCount] = useState(5);
  const [buyingSeats, setBuyingSeats] = useState(false);

  const authHdr = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [ov, pr] = await Promise.all([
        axios.get(`${API}/school-admin/overview`, authHdr()),
        axios.get(`${API}/school-admin/parents`, authHdr()),
      ]);
      setOverview(ov.data);
      setParents(pr.data?.parents || []);
    } catch (err) {
      if (err?.response?.status === 403) {
        toast.error('School admin access only');
        navigate('/');
      } else {
        toast.error('Failed to load dashboard');
      }
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/login');
  };

  const inviteParent = async () => {
    if (!pEmail.trim()) return toast.error("Enter the parent's email");
    setSubmitting(true);
    try {
      const r = await axios.post(`${API}/school-admin/invite-parent`,
        { email: pEmail.trim().toLowerCase(), name: pName.trim() || null }, authHdr());
      toast.success(r.data.email_sent ? 'Invite email sent' : 'Invited (copy the link below)');
      setPEmail(''); setPName('');
      setDlg(null);
      fetchAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not invite');
    } finally { setSubmitting(false); }
  };

  const inviteTherapist = async () => {
    if (!tEmail.trim() || !tName.trim()) return toast.error("Enter therapist name and email");
    setSubmitting(true);
    try {
      const r = await axios.post(`${API}/school-admin/invite-therapist`,
        { email: tEmail.trim().toLowerCase(), name: tName.trim() }, authHdr());
      toast.success(r.data.email_sent
        ? 'Therapist invited (email sent)'
        : `Invited. Share this link: ${r.data.invite_url}`);
      setTEmail(''); setTName('');
      setDlg(null);
      fetchAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not invite');
    } finally { setSubmitting(false); }
  };

  const startPaystackCheckout = async () => {
    const n = Number(buySeatsCount);
    if (!Number.isFinite(n) || n < 1) return toast.error('Enter a valid seat count');
    if (!overview?.price_per_seat_cents) {
      return toast.error('Pricing not configured yet — contact PlayFirst support first');
    }
    setBuyingSeats(true);
    try {
      const r = await axios.post(
        `${API}/practice/pfp-seats/checkout`,
        { seats: Math.floor(n), billing_cycle: 'monthly' }, authHdr(),
      );
      const url = r.data?.authorization_url;
      if (!url) return toast.error('Paystack returned no checkout URL');
      window.location.href = url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not start checkout');
    } finally { setBuyingSeats(false); }
  };

  const copyEmails = async () => {
    const list = parents.map((p) => p.email).filter(Boolean).join(', ');
    if (!list) return toast.error('No parent emails yet');
    await navigator.clipboard.writeText(list);
    toast.success(`Copied ${parents.length} email${parents.length === 1 ? '' : 's'}`);
  };

  const downloadCsv = () => {
    if (!parents.length) return toast.error('No parents yet');
    const headers = ['email', 'name', 'joined_at', 'whatsapp_verified', 'whatsapp_paused', 'last_active_at'];
    const lines = [headers.join(',')];
    parents.forEach((p) => {
      const row = [p.email, p.name, p.created_at, p.whatsapp_verified, p.whatsapp_paused, p.last_active_at]
        .map((v) => (v === null || v === undefined ? '' : String(v).includes(',') ? `"${v}"` : v));
      lines.push(row.join(','));
    });
    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${(overview?.school_name || 'school').replace(/\W+/g, '_')}_parents.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <nav className="bg-white border-b border-stone-100 sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center">
              <School className="w-5 h-5 text-emerald-700" />
            </div>
            <div>
              <h1 className="text-xl font-serif text-primary-900">
                {overview?.school_name || 'School Dashboard'}
              </h1>
              <p className="text-xs text-stone-500">
                {overview?.plan_type === 'pfp_only' ? 'PlayFirst Parent · School Admin' : ''}
              </p>
            </div>
          </div>
          <Button variant="ghost" onClick={logout} className="rounded-full" data-testid="logout-btn">
            <LogOut className="w-4 h-4 mr-2" /> Logout
          </Button>
        </div>
      </nav>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-6 h-6 animate-spin text-stone-400" />
          </div>
        ) : (
          <>
            {/* Seat overview */}
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
              <Card className="p-6 rounded-3xl bg-gradient-to-br from-emerald-50 to-teal-50 border-emerald-100" data-testid="seats-panel">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <div>
                    <p className="text-sm text-emerald-700 uppercase tracking-wide">PlayFirst Parent seats</p>
                    <p className="text-3xl font-serif text-emerald-900 mt-1">
                      {overview.seats_used} <span className="text-stone-400">/</span> {overview.seats_purchased || 0}
                      <span className="text-sm text-stone-600 ml-2">used</span>
                    </p>
                    <p className="text-sm text-stone-600 mt-1">
                      {overview.pfp_active
                        ? `Active · ${fmtCurrency(overview.price_per_seat_cents)} / seat / ${overview.billing_cycle}`
                        : overview.price_per_seat_cents
                          ? `Not subscribed yet — ${fmtCurrency(overview.price_per_seat_cents)} / seat / ${overview.billing_cycle}. Press "Buy seats" below.`
                          : 'Pricing not configured yet — contact PlayFirst support.'}
                    </p>
                  </div>
                  <div className="flex flex-col items-end gap-2">
                    <div className="text-right">
                      <p className="text-3xl font-serif text-emerald-800">{overview.seats_available}</p>
                      <p className="text-xs text-emerald-700">seats available</p>
                    </div>
                    {overview.price_per_seat_cents && (
                      <Button
                        onClick={() => setDlg('buy-seats')}
                        className="rounded-full bg-emerald-600 hover:bg-emerald-700"
                        data-testid="open-buy-seats-btn"
                      >
                        {overview.pfp_active ? 'Adjust seats' : 'Buy seats'}
                      </Button>
                    )}
                  </div>
                </div>
              </Card>
            </motion.div>

            {/* Actions row */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Card className="p-5 rounded-3xl border-stone-100 bg-white">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 rounded-2xl bg-primary/10 flex items-center justify-center">
                    <Users className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <p className="font-medium">Parents</p>
                    <p className="text-xs text-stone-500">{overview.parents_sponsored} sponsored by your school</p>
                  </div>
                </div>
                <Button
                  className="w-full rounded-full mt-3"
                  onClick={() => setDlg('invite-parent')}
                  data-testid="invite-parent-btn"
                  disabled={!overview.pfp_active}
                >
                  <UserPlus className="w-4 h-4 mr-2" /> Invite a parent
                </Button>
                {!overview.pfp_active && (
                  <p className="text-xs text-amber-700 mt-2">
                    Contact PlayFirst to activate seats before inviting parents.
                  </p>
                )}
              </Card>
              <Card className="p-5 rounded-3xl border-stone-100 bg-white">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 rounded-2xl bg-purple-100 flex items-center justify-center">
                    <GraduationCap className="w-5 h-5 text-purple-700" />
                  </div>
                  <div>
                    <p className="font-medium">Therapists</p>
                    <p className="text-xs text-stone-500">
                      {overview.therapists_count} therapist{overview.therapists_count === 1 ? '' : 's'} setting weekly goals
                    </p>
                  </div>
                </div>
                <Button
                  variant="outline"
                  className="w-full rounded-full mt-3"
                  onClick={() => setDlg('invite-therapist')}
                  data-testid="invite-therapist-btn"
                >
                  <Sparkles className="w-4 h-4 mr-2" /> Invite a therapist
                </Button>
              </Card>
            </div>

            {/* Parent list */}
            <Card className="p-6 rounded-3xl border-stone-100 bg-white">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 mb-4">
                <div>
                  <h3 className="text-lg font-serif text-primary-900">Parents at your school</h3>
                  <p className="text-xs text-stone-500">Read-only. No clinical data.</p>
                </div>
                {parents.length > 0 && (
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={copyEmails} className="rounded-full" data-testid="copy-emails-btn">
                      <Copy className="w-4 h-4 mr-1" /> Copy emails
                    </Button>
                    <Button variant="outline" size="sm" onClick={downloadCsv} className="rounded-full" data-testid="csv-btn">
                      <Download className="w-4 h-4 mr-1" /> CSV
                    </Button>
                  </div>
                )}
              </div>

              {parents.length === 0 ? (
                <div className="p-8 text-center border-2 border-dashed border-stone-200 rounded-2xl">
                  <Users className="w-8 h-8 mx-auto mb-2 text-stone-300" />
                  <p className="text-sm text-stone-500">
                    No parents sponsored yet. Invite your first parent above.
                  </p>
                </div>
              ) : (
                <div className="divide-y divide-stone-100">
                  {parents.map((p) => (
                    <div key={p.id} className="py-3 flex items-center justify-between gap-3" data-testid={`parent-row-${p.id}`}>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="font-medium text-sm text-primary-900 truncate">{p.email}</p>
                          {p.whatsapp_verified ? (
                            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-emerald-50 text-emerald-700 text-xs rounded-full">
                              <MessageCircle className="w-3 h-3" /> WhatsApp
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-stone-100 text-stone-500 text-xs rounded-full">
                              Awaiting WhatsApp
                            </span>
                          )}
                          {p.whatsapp_paused && (
                            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-amber-50 text-amber-700 text-xs rounded-full">
                              <PauseCircle className="w-3 h-3" /> Paused
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-stone-500">
                          {p.name} · Joined {fmtDate(p.created_at)}
                          {p.last_active_at && ` · Last active ${fmtDate(p.last_active_at)}`}
                        </p>
                      </div>
                      <Button asChild variant="ghost" size="sm" className="rounded-full">
                        <a href={`mailto:${p.email}`}>
                          <Mail className="w-4 h-4" />
                        </a>
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </Card>
          </>
        )}
      </main>

      {/* Invite parent dialog */}
      <Dialog open={dlg === 'invite-parent'} onOpenChange={(v) => !v && setDlg(null)}>
        <DialogContent className="rounded-3xl max-w-md">
          <DialogHeader><DialogTitle className="font-serif text-xl">Invite a parent</DialogTitle></DialogHeader>
          <div className="space-y-3 mt-2">
            <p className="text-sm text-stone-600">
              We&apos;ll email them a magic-link. Uses 1 PFP seat once they accept.
            </p>
            <div>
              <Label>Parent email</Label>
              <Input value={pEmail} onChange={(e) => setPEmail(e.target.value)} type="email" className="h-12 rounded-xl mt-1" placeholder="parent@example.com" data-testid="new-parent-email" />
            </div>
            <div>
              <Label>Parent name (optional)</Label>
              <Input value={pName} onChange={(e) => setPName(e.target.value)} className="h-12 rounded-xl mt-1" placeholder="e.g. Sarah Ndlovu" data-testid="new-parent-name" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-full" onClick={() => setDlg(null)}>Cancel</Button>
            <Button className="rounded-full" onClick={inviteParent} disabled={submitting} data-testid="send-parent-invite">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <UserPlus className="w-4 h-4 mr-2" />}
              Send invite
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Invite therapist dialog */}
      <Dialog open={dlg === 'invite-therapist'} onOpenChange={(v) => !v && setDlg(null)}>
        <DialogContent className="rounded-3xl max-w-md">
          <DialogHeader><DialogTitle className="font-serif text-xl">Invite a therapist</DialogTitle></DialogHeader>
          <div className="space-y-3 mt-2">
            <p className="text-sm text-stone-600">
              Your therapist gets a lightweight PlayFirst account — just enough to
              set 3 weekly priority goals for each child. No clinical dashboard.
            </p>
            <div>
              <Label>Therapist name</Label>
              <Input value={tName} onChange={(e) => setTName(e.target.value)} className="h-12 rounded-xl mt-1" placeholder="e.g. Dr Alice Mokoena" data-testid="new-therapist-name" />
            </div>
            <div>
              <Label>Therapist email</Label>
              <Input value={tEmail} onChange={(e) => setTEmail(e.target.value)} type="email" className="h-12 rounded-xl mt-1" placeholder="therapist@example.com" data-testid="new-therapist-email" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-full" onClick={() => setDlg(null)}>Cancel</Button>
            <Button className="rounded-full" onClick={inviteTherapist} disabled={submitting} data-testid="send-therapist-invite">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Sparkles className="w-4 h-4 mr-2" />}
              Send invite
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Buy seats via Paystack */}
      <Dialog open={dlg === 'buy-seats'} onOpenChange={(v) => !v && setDlg(null)}>
        <DialogContent className="rounded-3xl max-w-md">
          <DialogHeader><DialogTitle className="font-serif text-xl">Buy PFP seats</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-2">
            <p className="text-sm text-stone-600">
              You&apos;ll be redirected to Paystack to enter your card. After payment,
              your seats activate immediately and you can start inviting parents.
            </p>
            <div className="p-4 bg-emerald-50 rounded-2xl border border-emerald-200">
              <p className="text-sm text-emerald-900">
                Your rate: <strong>{fmtCurrency(overview?.price_per_seat_cents)}</strong>{' '}
                per seat per {overview?.billing_cycle}
              </p>
            </div>
            <div>
              <Label>Number of seats</Label>
              <Input
                type="number"
                min="1"
                max="1000"
                value={buySeatsCount}
                onChange={(e) => setBuySeatsCount(e.target.value)}
                className="h-12 rounded-xl mt-1"
                data-testid="buy-seats-count"
              />
              {overview?.price_per_seat_cents && (
                <p className="text-xs text-stone-500 mt-2">
                  Total: <strong>
                    {fmtCurrency((Number(buySeatsCount || 0)) * overview.price_per_seat_cents)}
                  </strong> per {overview.billing_cycle}
                </p>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-full" onClick={() => setDlg(null)}>Cancel</Button>
            <Button
              className="rounded-full bg-emerald-600 hover:bg-emerald-700"
              onClick={startPaystackCheckout}
              disabled={buyingSeats}
              data-testid="start-paystack-btn"
            >
              {buyingSeats ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Continue to Paystack
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
